import React, { useState } from 'react';
import { X, Banknote, AlertCircle, CheckCircle } from 'lucide-react';
import { createWithdrawalRequest } from '../lib/admin';
import type { Registry } from '../types/registry';

interface Props {
  registry: Registry;
  maxAmount: number;
  onClose: () => void;
  onSuccess: () => void;
}

export default function WithdrawalRequestModal({ registry, maxAmount, onClose, onSuccess }: Props) {
  const [amount, setAmount] = useState(maxAmount.toFixed(2));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = parseFloat(amount);

    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Please enter a valid amount greater than zero');
      return;
    }

    if (parsedAmount > maxAmount) {
      setError(`Amount cannot exceed ${registry.currency} ${maxAmount.toFixed(2)}`);
      return;
    }

    try {
      setLoading(true);
      setError('');
      await createWithdrawalRequest(registry.id, parsedAmount);
      onSuccess();
    } catch (err) {
      console.error('Error creating withdrawal request:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit withdrawal request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-50">
      <div className="card-pastel p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2 font-poppins">
            <Banknote className="w-5 h-5 text-pastel-green-600" />
            Request Withdrawal
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/60 rounded-full transition-colors text-gray-500 hover:text-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span className="text-sm font-poppins">{error}</span>
          </div>
        )}

        <div className="mb-4 bg-white/60 rounded-lg p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 font-poppins">Available Balance:</span>
            <span className="text-pastel-green-600 font-medium font-poppins">{registry.currency} {maxAmount.toFixed(2)}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
              Withdrawal Amount ({registry.currency})
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={maxAmount}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
              required
            />
            <p className="text-xs text-gray-500 mt-1 font-poppins">
              You can request a partial or full withdrawal. The admin will review and process your request.
            </p>
          </div>

          <div className="bg-white/60 rounded-lg p-4 space-y-2">
            <h4 className="text-sm font-medium text-gray-600 font-poppins">Bank Details</h4>
            <div className="space-y-1 text-sm font-poppins">
              <div className="flex justify-between">
                <span className="text-gray-500">Account Holder:</span>
                <span className="text-gray-800">{registry.account_holder_name || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Account Number:</span>
                <span className="text-gray-800">{registry.account_number ? `****${registry.account_number.slice(-4)}` : 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Bank:</span>
                <span className="text-gray-800">{registry.bank_name || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Branch:</span>
                <span className="text-gray-800">{registry.bank_branch_name || 'Not set'}</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 pt-1 font-poppins">
              These are the bank details from your registry. Contact the admin if they need updating.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 font-poppins"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || maxAmount <= 0}
              className="px-6 py-2 bg-pastel-green-600 text-white rounded-md hover:bg-pastel-green-500 disabled:opacity-50 flex items-center gap-2 font-poppins"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Submit Request</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}